import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Alert,
  Tabs,
  Divider,
  Row,
  Col,
  message
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined,
  LoginOutlined,
  UserAddOutlined,
  HomeOutlined
} from '@ant-design/icons';
import './Auth.css';

const { Title, Text, Link } = Typography;
const { TabPane } = Tabs;

const Auth = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // 从location state获取初始标签页
  useEffect(() => {
    if (location.state?.tab === 'register') {
      setActiveTab('register');
    }
  }, [location]);

  // 从localStorage获取已注册用户
  const getUsersFromStorage = () => {
    const users = localStorage.getItem('blog_users');
    return users ? JSON.parse(users) : [];
  };

  // 处理登录
  const handleLogin = async (values) => {
    setLoading(true);

    try {
      const users = getUsersFromStorage();
      const currentUser = localStorage.getItem('blog_current_user');

      // 检查是否已登录
      if (currentUser) {
        messageApi.warning('您已经登录了，请先注销再重新登录');
        setLoading(false);
        return;
      }

      // 验证用户
      const user = users.find(u => 
        u.username === values.username && 
        u.password === values.password
      );

      if (user) {
        // 保存登录状态
        localStorage.setItem('blog_current_user', JSON.stringify(user));
        
        messageApi.success('登录成功！正在跳转...');

        // 跳转到之前尝试访问的页面或首页
        const from = location.state?.from || '/';
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 1500);
      } else {
        messageApi.error('用户名或密码错误');
      }
    } catch (error) {
      messageApi.error('登录失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理注册
  const handleRegister = async (values) => {
    setLoading(true);

    try {
      const users = getUsersFromStorage();

      // 检查用户名是否已存在
      const userExists = users.some(u => u.username === values.username);
      if (userExists) {
        messageApi.error('用户名已存在，请选择其他用户名');
        setLoading(false);
        return;
      }

      // 检查邮箱是否已存在
      const emailExists = users.some(u => u.email === values.email);
      if (emailExists) {
        messageApi.error('邮箱已被注册，请使用其他邮箱');
        setLoading(false);
        return;
      }

      // 创建新用户
      const newUser = {
        id: Date.now(),
        username: values.username,
        email: values.email,
        password: values.password,
        name: values.username,
        role: 'user', // 普通用户
        createdAt: new Date().toISOString(),
        // 默认头像和背景色
        avatar: '',
        avatarColor: '#4a90e2',
        bio: '这个人很神秘，什么也没留下。',
        position: '未设置',
        location: '未设置',
        joinDate: new Date().toLocaleDateString('zh-CN'),
        socialMedia: '未设置',
        skills: ['React', 'JavaScript'],
        techStack: [
          { name: 'React', level: 50 },
          { name: 'JavaScript', level: 60 }
        ]
      };

      // 保存用户到本地存储
      const updatedUsers = [...users, newUser];
      localStorage.setItem('blog_users', JSON.stringify(updatedUsers));

      messageApi.success('注册成功！请登录');

      // 切换到登录标签页
      setTimeout(() => {
        setActiveTab('login');
        form.setFieldsValue({
          username: values.username,
          password: ''
        });
      }, 1000);
    } catch (error) {
      messageApi.error('注册失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理标签页切换
  const handleTabChange = (key) => {
    setActiveTab(key);
    form.resetFields();
  };

  // 处理忘记密码
  const handleForgotPassword = () => {
    messageApi.info('请联系管理员重置密码');
  };

  // 快速登录测试账户
  const handleQuickLogin = (type) => {
    const testUsers = {
      admin: { username: 'admin', password: '123456' },
      user: { username: 'user', password: '123456' }
    };

    form.setFieldsValue(testUsers[type]);
    messageApi.info(`已填充${type === 'admin' ? '管理员' : '普通用户'}测试账号，请点击登录`);
  };

  return (
    <>
      {contextHolder}
      <div className="auth-container">
        <div className="auth-header">
          <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>
            <LoginOutlined style={{ marginRight: 12, color: '#1890ff' }} />
            博客系统登录
          </Title>
          <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
            欢迎访问我的技术博客，请先登录或注册
          </Text>
        </div>

        <Row justify="center" style={{ marginTop: 40 }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Card
              className="auth-card"
              bordered={false}
              style={{
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: 8
              }}
            >
              <Tabs 
                activeKey={activeTab} 
                onChange={handleTabChange}
                centered
              >
                {/* 登录标签页 */}
                <TabPane 
                  tab={
                    <span>
                      <LoginOutlined />
                      登录
                    </span>
                  } 
                  key="login"
                >
                  <Form
                    form={form}
                    name="login"
                    onFinish={handleLogin}
                    layout="vertical"
                    initialValues={{
                      remember: true
                    }}
                  >
                    <Form.Item
                      name="username"
                      label="用户名"
                      rules={[
                        { required: true, message: '请输入用户名' },
                        { min: 3, message: '用户名至少3个字符' },
                        { max: 20, message: '用户名最多20个字符' }
                      ]}
                    >
                      <Input 
                        prefix={<UserOutlined />} 
                        placeholder="请输入用户名"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="密码"
                      rules={[
                        { required: true, message: '请输入密码' },
                        { min: 6, message: '密码至少6个字符' }
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="请输入密码"
                        size="large"
                      />
                    </Form.Item>

                    <div style={{ marginBottom: 24, textAlign: 'right' }}>
                      <Button 
                        type="link" 
                        onClick={handleForgotPassword}
                        style={{ padding: 0 }}
                      >
                        忘记密码？
                      </Button>
                    </div>

                    <Form.Item>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading}
                        block
                        size="large"
                      >
                        登录
                      </Button>
                    </Form.Item>

                    <Divider>快速登录</Divider>

                    <Space style={{ width: '100%', justifyContent: 'center' }}>
                      <Button 
                        onClick={() => handleQuickLogin('admin')}
                        icon={<UserOutlined />}
                      >
                        管理员账号
                      </Button>
                      <Button 
                        onClick={() => handleQuickLogin('user')}
                        icon={<UserOutlined />}
                      >
                        普通用户账号
                      </Button>
                    </Space>
                  </Form>
                </TabPane>

                {/* 注册标签页 */}
                <TabPane 
                  tab={
                    <span>
                      <UserAddOutlined />
                      注册
                    </span>
                  } 
                  key="register"
                >
                  <Form
                    form={form}
                    name="register"
                    onFinish={handleRegister}
                    layout="vertical"
                  >
                    <Form.Item
                      name="username"
                      label="用户名"
                      rules={[
                        { required: true, message: '请输入用户名' },
                        { min: 3, message: '用户名至少3个字符' },
                        { max: 20, message: '用户名最多20个字符' },
                        { pattern: /^[a-zA-Z0-9_]+$/, message: '只能包含字母、数字和下划线' }
                      ]}
                    >
                      <Input 
                        prefix={<UserOutlined />} 
                        placeholder="请输入用户名"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label="邮箱"
                      rules={[
                        { required: true, message: '请输入邮箱' },
                        { type: 'email', message: '请输入有效的邮箱地址' }
                      ]}
                    >
                      <Input 
                        prefix={<MailOutlined />} 
                        placeholder="请输入邮箱"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      label="密码"
                      rules={[
                        { required: true, message: '请输入密码' },
                        { min: 6, message: '密码至少6个字符' }
                      ]}
                      hasFeedback
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="请输入密码"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirm"
                      label="确认密码"
                      dependencies={['password']}
                      hasFeedback
                      rules={[
                        { required: true, message: '请确认密码' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('两次输入的密码不一致'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="请确认密码"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={loading}
                        block
                        size="large"
                      >
                        注册
                      </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center' }}>
                      <Text type="secondary">
                        已有账户？{' '}
                        <Button 
                          type="link" 
                          onClick={() => setActiveTab('login')}
                          style={{ padding: 0 }}
                        >
                          立即登录
                        </Button>
                      </Text>
                    </div>
                  </Form>
                </TabPane>
              </Tabs>

              <Divider style={{ margin: '24px 0' }} />

              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">
                  登录即表示您同意我们的{' '}
                  <Link href="#" target="_blank">服务条款</Link>{' '}
                  和{' '}
                  <Link href="#" target="_blank">隐私政策</Link>
                </Text>
              </div>
            </Card>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Button 
                type="text" 
                icon={<HomeOutlined />}
                onClick={() => navigate('/')}
                style={{ color: '#1890ff' }}
              >
                返回首页
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Auth;